import React from 'react';
import { useSWRConfig } from 'swr';
import cloneDeep from 'lodash.clonedeep';
import { Col, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { HubConnection } from '@microsoft/signalr';

import { GameContext } from '../../contexts/game';
import { submitScoringAsync } from '../../services/players';
import { ManualScoring } from '../../types/manual-scoring';
import { GameDocumentContext } from '../../contexts/game-document';
import { GetTaskContentByTaskId } from '../../utils/game-engine/player';
import { DetailPlayer } from '../../components/facilitator/detail-player';
import { useGetManualScoringAnswers } from '../../hooks/use-get-manual-scoring';
import { GetTaskContentById } from '../../utils/game-document/assets/task-contents';
import { FacilitatorScoringList } from '../../components/facilitator/facilitator-scoring-list';

export const TaskScoringPage = () => {
  const { gameCode } = useParams();
  const { mutate } = useSWRConfig();

  const [game, , gameStateConnection] = React.useContext(GameContext);
  const [gameDocumentState] = React.useContext(GameDocumentContext);

  const [, setTaskScorings, removeTaskScorings] = useLocalStorage<
    ManualScoring[]
  >(`task-scorings-${gameCode}`, []);
  const [selectedManualScoring, setSelectedManualScoring] = React.useState<
    ManualScoring | undefined
  >();

  const { data: taskScorings, isLoading } =
    useGetManualScoringAnswers(gameCode);

  const taskList = (gameDocumentState.gameDocument?.assets.tasks ?? []).map(
    (t) => t.id
  );

  const isOnDemandTask = React.useMemo(
    () => !taskList.includes(selectedManualScoring?.taskId ?? ''),
    [taskList, selectedManualScoring?.taskId]
  );

  const handleChange = (manualScoring: ManualScoring) => {
    setSelectedManualScoring(manualScoring);
  };

  const submitScoring = async (scoring: ManualScoring) => {
    try {
      await submitScoringAsync(scoring);
    } catch (error) {
      console.error('Failed submit scoring');
    }
  };

  const onSubmitScoreHandler = async (
    scoring: ManualScoring,
    type: 'Task' | 'OnDemandTask'
  ) => {
    // handle if facilitator submit score without change the form
    // score will be undefined, so use min score instead
    if (scoring.score === undefined) {
      const taskContent =
        type === 'Task'
          ? GetTaskContentByTaskId(
              gameDocumentState.gameDocument!,
              scoring.taskId ?? ''
            )
          : GetTaskContentById(
              gameDocumentState.gameDocument,
              scoring.taskId ?? ''
            );

      if (taskContent) {
        if (taskContent.ARMiniGameSetting) {
          scoring.score = taskContent.ARMiniGameSetting.manualScoring?.min;
        } else {
          scoring.score =
            taskContent.forms?.find((f) => f.id === scoring.formId)
              ?.manualScoring?.min ?? 0;
        }
      }
    }

    submitScoring(scoring);
    const scoringIndex = taskScorings?.findIndex(
      (x) =>
        x.playerCode === scoring?.playerCode &&
        x.taskId === scoring?.taskId &&
        x.formId === scoring?.formId
    );

    if (scoringIndex !== -1) {
      const newScoring: ManualScoring[] = cloneDeep(taskScorings);
      newScoring.splice(scoringIndex, 1);

      if (game.gameState?.status === 'Finished' && taskScorings.length === 1) {
        removeTaskScorings();
      } else {
        setTaskScorings((prev) =>
          prev.filter(
            (t) =>
              !(
                t.formId === scoring.formId &&
                t.playerCode === scoring.playerCode &&
                t.taskId === scoring.taskId
              )
          )
        );
      }

      setSelectedManualScoring(
        newScoring.length > 0 ? newScoring[0] : undefined
      );
    }
  };

  const gameStateListener = (connection: HubConnection) => {
    connection.on('AnswerSubmitted', () => {
      mutate(`games/${gameCode}/players/answer`);
    });

    connection.onclose((e) => {
      console.error('error');
    });
  };

  React.useEffect(() => {
    if (!gameStateConnection) return;
    gameStateListener(gameStateConnection);
  }, [gameStateConnection]);

  return (
    <div
      className={'d-flex flex-column flex-grow-1 h-100 w-100 overflow-hidden'}>
      {isLoading && (
        <div
          className={
            'd-flex flex-column align-items-center justify-content-center h-100 w-100 gap-2'
          }>
          <span className={'loader'}></span>
          <label className={'text-gray'}>Loading...</label>
        </div>
      )}
      {taskScorings.length === 0 ? (
        <h3>No scoring found</h3>
      ) : (
        <Row className={'flex-grow-1 h-100 m-0'}>
          <Col
            md={3}
            className={
              'p-0 border border-secondary d-flex flex-column h-100 overflow-auto'
            }>
            <FacilitatorScoringList
              listData={taskScorings}
              onClick={handleChange}
              selectedId={selectedManualScoring?.id as number}
            />
          </Col>
          <Col md={9} className={'border border-secondary h-100'}>
            {selectedManualScoring !== undefined && (
              <DetailPlayer
                key={`${selectedManualScoring.playerCode}-${selectedManualScoring.formId}-${selectedManualScoring.id}`}
                type={isOnDemandTask ? 'OnDemandTask' : 'Task'}
                dataPlayer={selectedManualScoring}
                onSubmitScore={onSubmitScoreHandler}
                displayDate
              />
            )}
          </Col>
        </Row>
      )}
    </div>
  );
};
